import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Pause, Play, Trash2, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AccentSelect } from './LanguageAccentCard';
import { absUrl, actorVoicePreviewUrl, deleteActor, errMsg, listActors, patchActor } from '../../lib/aiStudioApi';

const GENDER = { F: 'Female', M: 'Male', N: 'Neutral' };

/** Play a voice sample (one cached sample per voice, shared by every actor and production). */
export function VoicePlay({ voiceId, testId }) {
  const audio = useRef(null);
  const [state, setState] = useState('idle');
  useEffect(() => () => audio.current?.pause(), []);
  const toggle = () => {
    if (state === 'playing') { audio.current?.pause(); setState('idle'); return; }
    audio.current = new Audio(actorVoicePreviewUrl(voiceId));
    audio.current.onended = () => setState('idle');
    audio.current.onerror = () => { setState('idle'); toast.error('Voice sample unavailable right now'); };
    setState('loading');
    audio.current.play().then(() => setState('playing')).catch(() => setState('idle'));
  };
  return (
    <button type="button" onClick={toggle} data-testid={testId} aria-label="Play voice sample"
      className="flex h-6 w-6 items-center justify-center rounded-full border border-neutral-700 text-neutral-300 transition-colors duration-200 hover:border-amber-400 hover:text-amber-300">
      {state === 'playing' ? <Pause size={10} /> : <Play size={10} className={state === 'loading' ? 'animate-pulse' : ''} />}
    </button>
  );
}

function ActorRow({ actor, voices, accents, onChanged }) {
  const face = absUrl(actor.face_url);
  const mine = voices.filter((v) => v.gender === actor.gender || v.gender === 'N');
  const setVoice = async (voice_id) => {
    try {
      const r = await patchActor(actor.id, { voice_id });
      toast.success(`${actor.name} now speaks as ${r.voice?.eleven_name}${r.productions_updated ? ` · ${r.productions_updated} production(s) will re-voice their lines` : ''}`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setAccent = async (accent) => {
    try {
      const r = await patchActor(actor.id, { accent });
      toast.success(`${actor.name}'s accent saved${r.productions_updated ? ` · ${r.productions_updated} production(s) will re-voice their lines` : ''}`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await deleteActor(actor.id); toast.success(`${actor.name} left the roster`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-3 border-b border-neutral-900 px-1 py-2.5" data-testid={`actor-row-${actor.id}`}>
      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full border border-neutral-800 bg-neutral-900">
        {face ? <img src={face} alt={actor.name} className="h-full w-full object-cover" /> : <Users size={16} className="m-3.5 text-neutral-600" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-neutral-100">{actor.name}</div>
        <div className="truncate text-[11px] text-neutral-500">{GENDER[actor.gender]} · {actor.age} · {actor.productions} production{actor.productions === 1 ? '' : 's'}</div>
      </div>
      <AccentSelect accents={accents} value={actor.accent || ''} onChange={setAccent} inherit="Production default" label="" className="w-48 shrink-0" testId={`actor-accent-${actor.id}`} />
      <select value={actor.voice?.eleven_id} onChange={(e) => setVoice(e.target.value)} data-testid={`actor-voice-${actor.id}`}
        className="w-44 rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1.5 text-xs text-neutral-200">
        {mine.map((v) => {
          const taken = v.used_by.length && v.id !== actor.voice?.eleven_id;
          return <option key={v.id} value={v.id} disabled={!!taken}>{v.name} · {v.accent}, {v.tone}{taken ? ` — ${v.used_by[0]}` : ''}</option>;
        })}
      </select>
      <VoicePlay voiceId={actor.voice?.eleven_id} testId={`actor-voice-play-${actor.id}`} />
      <button type="button" onClick={remove} className="text-neutral-600 transition-colors duration-200 hover:text-red-300" data-testid={`actor-delete-${actor.id}`}><Trash2 size={13} /></button>
    </div>
  );
}

/** Tenant-wide roster: each actor keeps one face, look and unique voice across every episode, series and movie. */
export function ActorRoster({ open, onOpenChange, onChanged }) {
  const [data, setData] = useState({ actors: [], voices: [], accents: [] });
  const load = () => listActors().then(setData).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (open) load(); }, [open]);
  const changed = () => { load(); onChanged?.(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-dark max-w-3xl border-neutral-800 bg-neutral-950 text-neutral-100" data-testid="actor-roster-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users size={16} className="text-amber-300" /> Actor roster</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-400">Every actor keeps the same face, look and voice in every episode, series and movie. No two actors share a voice.
          The AI Director casts from this roster whenever an actor fits a role.</p>
        <div className="max-h-[55vh] overflow-y-auto" data-testid="actor-roster-list">
          {data.actors.length ? data.actors.map((a) => <ActorRow key={a.id} actor={a} voices={data.voices} accents={data.accents} onChanged={changed} />)
            : <div className="py-8 text-center text-xs text-neutral-500">No actors yet. The AI Director signs them when it writes your first screenplay.</div>}
        </div>
        <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="border-neutral-700" data-testid="actor-roster-close">Done</Button></div>
      </DialogContent>
    </Dialog>
  );
}
